import { useCallback, useEffect, useRef } from 'react';
import { useUpdateStore } from '../store/updateStore';
import {
  checkForUpdates,
  downloadAndInstall,
  skipUpdateVersion,
  recordUpdatePromptShown,
  readUpdatePromptPrefs,
  RECHECK_INTERVAL_MS,
} from '../services/updateService';
import { describeUpdateCheckResult, utcDayStamp } from '../utils/updateUtils';
import { decideUpdatePrompt } from '../utils/updatePromptPolicy';
import { isTauri } from '../utils/tauri';

/**
 * Owns app auto-update lifecycle: the initial + periodic background check, and
 * the manual check / open / install / later / skip handlers. State lives in
 * updateStore; writes go through getState() so the callbacks stay stable.
 *
 * WHEN the dialog may announce itself is not decided here — that is
 * `utils/updatePromptPolicy.js`, which also carries the history of why closing
 * must never dismiss and why the badge stays lit after "Later".
 *
 * @param {{ showToast: Function, t: Function }} deps
 */
export function useAppUpdates({ showToast, t }) {
  const updateInfo = useUpdateStore((s) => s.updateInfo);
  const updateModalOpen = useUpdateStore((s) => s.updateModalOpen);
  const updateInstalling = useUpdateStore((s) => s.updateInstalling);
  const updateError = useUpdateStore((s) => s.updateError);

  const runUpdateCheck = useCallback(async () => {
    if (!isTauri()) return;
    const result = await checkForUpdates({ quiet: true });
    useUpdateStore.getState().setUpdateInfo(result);
  }, []);

  useEffect(() => {
    if (!isTauri()) return undefined;
    let cancelled = false;
    let interval;
    (async () => {
      // Settings → "Check for updates: Never" had no reader at all: the box
      // was honoured by nothing and the user was checked and prompted anyway.
      // `launch` vs `daily` remains unrepresentable while the once-per-UTC-day
      // manifest cache exists, and is a separate task.
      const { checkUpdatesFrequency } = await readUpdatePromptPrefs();
      if (cancelled || checkUpdatesFrequency === 'never') return;
      runUpdateCheck();
      interval = setInterval(runUpdateCheck, RECHECK_INTERVAL_MS);
    })();
    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
    };
  }, [runUpdateCheck]);

  /**
   * TELL THE USER, at most once per UTC day.
   *
   * The startup check already ran and already knew an update existed — it just
   * put the answer in the store and said nothing, so the only way to find out
   * was to go looking in Settings. A release nobody is told about is a release
   * nobody installs.
   *
   * The stamp is written HERE, at the announcement, and by nothing else. That
   * is what lets `onUpdateModalClose` and `handleUpdateLater` write nothing at
   * all while still not re-nagging the same day. See updatePromptPolicy.js.
   *
   * `announcedRef` is a second, narrower guard: it stops a re-render re-opening
   * a modal within one process. The day stamp is the guard across launches.
   * Two guards, two scopes.
   */
  const announcedRef = useRef(false);
  useEffect(() => {
    if (!isTauri()) return undefined;
    if (announcedRef.current) return undefined;
    if (!updateInfo?.updateAvailable) return undefined;

    let cancelled = false;
    (async () => {
      const prefs = await readUpdatePromptPrefs();
      if (cancelled || announcedRef.current) return;

      const today = utcDayStamp();
      const decision = decideUpdatePrompt({
        updateAvailable: Boolean(updateInfo.updateAvailable),
        // The artifact's own version, never the manifest headline — they differ
        // whenever an entry was carried forward from an earlier build.
        offeredVersion: updateInfo.artifactVersion || updateInfo.latest?.version || '',
        skippedVersion: prefs.skippedUpdateVersion,
        lastPromptedDay: prefs.updatePromptLastShownDay,
        today,
      });
      if (decision.action !== 'prompt') return;

      // Never announce over a dialog that is already open. This effect became
      // ASYNC when it started reading preferences, so it can now resolve after
      // the user has opened the modal and after an install has failed in it —
      // and the `setUpdateError('')` below would then wipe the very message
      // explaining why the install failed. Neither branch could see this
      // alone: the async read comes from the prompt-cadence work, the install
      // error path from the quit-on-install work.
      //
      // The user is already looking at the dialog, so they have been told.
      // Claim the announcement (so a re-render cannot re-open it later) and
      // leave the store exactly as it is — including the stamp, which records
      // announcements we actually made.
      const live = useUpdateStore.getState();
      if (live.updateModalOpen) {
        announcedRef.current = true;
        return;
      }
      // An install is running, or has just failed and left a message nobody has
      // read yet. Announcing would clear that message below. Do NOT claim the
      // announcement here — unlike the open-modal case the user has been told
      // nothing, so the next check should still be free to prompt.
      if (live.updateInstalling || live.updateError) return;

      announcedRef.current = true;
      await recordUpdatePromptShown(today);
      const store = useUpdateStore.getState();
      store.setUpdateError('');
      store.setUpdateModalOpen(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [updateInfo]);

  const handleOpenUpdateModal = useCallback(() => {
    // The user OPENED this. The daily cap is for ANNOUNCEMENTS only — somebody
    // who went looking gets the dialog whatever the stamp says, and opening it
    // records nothing.
    const store = useUpdateStore.getState();
    store.setUpdateError('');
    store.setUpdateModalOpen(true);
  }, []);

  const handleManualUpdateCheck = useCallback(async () => {
    // User-initiated check: bypass the once-per-UTC-day manifest cache, and go
    // to the UNCOUNTED `/updates/manifest`. Forcing the counted `/latest` made
    // a curious user clicking five times look like five active devices.
    const result = await checkForUpdates({
      ignoreDismissed: true,
      force: true,
      counted: false,
    });
    const store = useUpdateStore.getState();
    store.setUpdateInfo(result);

    const feedback = describeUpdateCheckResult(result, t);

    if (result.updateAvailable) {
      store.setUpdateError('');
      // A manual check IS the announcement for this version — claim it so the
      // startup effect does not re-open behind the user. It writes no day
      // stamp: the cap paces what the app says unprompted, not what the user
      // asked for.
      announcedRef.current = true;
      store.setUpdateModalOpen(true);
    }

    const toastType =
      feedback.variant === 'success'
        ? 'success'
        : feedback.variant === 'error'
          ? 'error'
          : 'info';
    showToast(feedback.message, toastType);

    return result;
  }, [showToast, t]);

  const handleUpdateLater = useCallback(() => {
    // "Later" means NOT NOW, and writes nothing. It used to write the
    // dismissal, which made the button labelled "Later" a permanent per-version
    // opt-out — the label was right, the behaviour behind it was not. The day
    // stamp was already written at announcement, so this closes for today; the
    // badge deliberately stays lit, and the dialog asks again tomorrow.
    useUpdateStore.getState().setUpdateModalOpen(false);
  }, []);

  const handleSkipVersion = useCallback(async () => {
    const store = useUpdateStore.getState();
    // Skip the version actually on offer for this platform — the same value
    // isUpdateAvailable compares the skip against. Skipping the headline
    // version would leave a carried-forward entry re-prompting forever.
    const offered =
      store.updateInfo?.artifactVersion || store.updateInfo?.latest?.version;
    if (offered) {
      await skipUpdateVersion(offered);
    }
    store.setUpdateModalOpen(false);
    // This one DOES darken the badge, and only for this exact version: an
    // explicit, informed refusal. No new badge plumbing — `checkForUpdates`
    // feeds the skip to `isUpdateAvailable`, which zeroes `updateAvailable`,
    // which is the boolean the badge reads. A newer release lights it again on
    // its own; the skip is never revived and never overwritten by a newer
    // version. Settings → Check for updates (ignoreDismissed) is the way back.
  }, []);

  const handleInstallUpdate = useCallback(async () => {
    const store = useUpdateStore.getState();
    const info = store.updateInfo;
    if (!info?.artifact || !info?.latest) return;
    store.setUpdateInstalling(true);
    store.setUpdateError('');
    try {
      // expectedVersion must be what THESE BYTES claim to be, not the manifest
      // headline. Rust refuses an artifact whose embedded version disagrees
      // with what it was told to expect, so handing it the headline version
      // would make every carried-forward entry fail the binding check.
      await downloadAndInstall(
        info.artifact,
        info.artifactVersion || info.latest.version
      );
      showToast(t('update.installerOpened'), 'success');
    } catch (err) {
      console.error('Update install failed:', err);
      store.setUpdateError(err?.message || t('update.installFailed'));
    } finally {
      // Cleared on BOTH paths. It used to be cleared only in the `catch`, on
      // the assumption that the success path ends with the process gone — but
      // the process did NOT go (close-to-tray swallowed the close), so the app
      // came back from the tray stuck on "Installing…" with no way out:
      // `onUpdateModalClose` returns early while this flag is set. Even with
      // the quit fixed, "normally gone in ~700 ms" is not "always gone" — the
      // browser build has no quit command at all — so the flag is cleared here
      // rather than being left to the process's death.
      store.setUpdateInstalling(false);
    }
  }, [showToast, t]);

  const onUpdateModalClose = useCallback(() => {
    const store = useUpdateStore.getState();
    if (store.updateInstalling) return;
    store.setUpdateModalOpen(false);
    // CLOSING THE DIALOG IS NOT DISMISSING THE UPDATE. Nothing is written here,
    // on purpose — it used to call dismissUpdate and that permanently opted the
    // user out of a version they had not decided about, badge and all. The full
    // history lives in the updatePromptPolicy.js docblock, beside the rule.
    //
    // The daily cap still holds, because the stamp was written when the modal
    // announced itself — not by this handler and not by any other.
  }, []);

  return {
    updateInfo,
    updateModalOpen,
    updateInstalling,
    updateError,
    onUpdateModalClose,
    handleOpenUpdateModal,
    handleManualUpdateCheck,
    handleUpdateLater,
    handleSkipVersion,
    handleInstallUpdate,
  };
}
